<template>
  <FramedContainer id="showcase"
      :colors="colors"
      :background-variant="backgroundVariant"
      class="h-full w-full relative transition-all duration-1000 ease-in-out"
      :class="[calculatedClass, { 'z-10 w-full': isActive, 'fullscreen bg-white': isFullscreen }]"
      @mouseenter="isActive = true"
      @mouseleave="isActive = false"
      @click="toggleActive"
      :less-translucent="isFullscreen"
  >
    <button v-if="isFullscreen"
            class="close-button absolute md:top-4 md:right-4 right-2 top-2 text-white rounded-full flex items-center justify-center md:h-8 md:w-8 h-6 w-6 cursor-pointer hover:bg-gray-300 transition hover:shadow-sm text-xs md:text-base hover:text-gray-800 z-20 bg-gray-600"
            @click="toggleFullscreen"
    >
      &times;
    </button>
    <div v-if="false" class="flex flex-col w-full h-full relative overflow-y-auto p-10">
      <button
          class="close-button absolute md:top-4 md:right-4 right-2 top-2 text-white rounded-full flex items-center justify-center md:h-8 md:w-8 h-6 w-6 cursor-pointer hover:bg-gray-300 transition hover:shadow-sm text-xs md:text-base hover:text-gray-800 z-20 bg-gray-600"
          @click="toggleFullscreen"
      >
        &times;
      </button>
      <div class=" rounded p-2 bg-white">

        <img class="w-full max-w-96 object-cover max-h-64 rounded" v-if="src.url !== ''" :src="resolveMediaUrl(src.url)" v-bind="imgAttrs(resolveMediaUrl(src.url), '384px')" :alt="imgAlt(resolveMediaUrl(src.url), title, alt)"/>
        <video class="w-full h-full object-cover rounded" v-else-if="video"
               :src="video.link"
               :poster="video.poster"
               loop
               muted
               playsinline
               autoplay
        >
        </video>
      </div>
      <div class="p-1  p-4 rounded">
        <component :is="headingTag" class="text-xl font-bold link-text text-[var(--bg-dark-variant-1)]">
          {{ title }}
        </component>
        <p class="mt-4 text-lg whitespace-pre-wrap text-[var(--bg-dark-variant-2)]">
          {{ detail || content }}
        </p>
        <WebButton @clicked="toggleFullscreen">
          Contact us
        </WebButton>
      </div>
    </div>
    <div v-if="isFullscreen" class="flex flex-col w-full h-full relative">
      <div class="w-full h-full">
        <img class="w-full max-h-[40rem] object-cover" v-if="src.url !== ''" :src="resolveMediaUrl(src.url)" v-bind="imgAttrs(resolveMediaUrl(src.url), '100vw')" :alt="imgAlt(resolveMediaUrl(src.url), title, alt)"/>
        <video class="w-full h-full object-cover rounded" v-else-if="video"
               :src="video.link"
               :poster="video.poster"
               loop
               muted
               playsinline
               autoplay
        ></video>
      </div>
      <div
          class="w-full absolute h-full sm:h-auto bottom-0 flex flex-col shadow-xl items-center justify-center transition-all duration-300 ease-in-out"
          :class="{ 'opacity-100 backdrop-blur-sm bg-black/50': isActive, 'opacity-0 backdrop-blur-0': !isActive }"
      >
        <div class="p-1 bg-opacity-50 p-4 rounded" v-if="true">
          <component :is="headingTag" class="md:text-xl md:font-bold font-semibold link-text text-white">
            {{ title }}
          </component>
          <p class="mt-4 text-sm md:text-lg text-white">
            {{ content }}
          </p>
        </div>
        <div class="w-full flex items-center justify-center mb-4">
          <WebButton @clicked="gotoPage">
            {{ callToAction || 'Learn more' }}
          </WebButton>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col w-full h-full relative">
      <div class="w-full h-full">
        <img class="w-full max-h-[40rem] object-cover" v-if="src.url !== ''" :src="resolveMediaUrl(src.url)" v-bind="imgAttrs(resolveMediaUrl(src.url), '100vw')" :alt="imgAlt(resolveMediaUrl(src.url), title, alt)"/>
        <video class="w-full h-full object-cover rounded" v-else-if="video"
               :src="video.link"
               :poster="video.poster"
               loop
               muted
               playsinline
               autoplay
        >
        </video>
      </div>
      <!-- The project's title and intro always show (they were only visible
           on hover, so on phones and at a glance the portfolio had no titles). -->
      <div
          class="w-full absolute bottom-0 flex flex-col items-start justify-end transition-all duration-300 ease-in-out showcase-caption"
          :class="isActive ? 'backdrop-blur bg-black/50' : 'bg-gradient-to-t from-black/70 via-black/40 to-transparent'"
      >
        <div class="p-4 w-full text-left">
          <button type="button" @click.stop="toggleFullscreen" class="text-left">
            <component :is="headingTag" class="md:text-xl md:font-bold font-semibold link-text text-white underline-offset-4 hover:underline cursor-pointer" data-testid="showcase-title">
              {{ title }}
            </component>
          </button>
          <p v-if="content" class="mt-1 text-sm text-white/90 line-clamp-2">{{ content }}</p>
          <div v-if="link" class="mt-3">
            <WebButton @clicked="gotoPage">{{ callToAction || 'Learn more' }}</WebButton>
          </div>
        </div>
      </div>
    </div>

  </FramedContainer>
</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed, onMounted, onUnmounted, ref} from 'vue';
import router from '@/router/index.js';
import FramedContainer from '@/components/FramedContainer.vue';
import WebButton from "@/components/WebButton.vue";
import {resolveMediaUrl} from "@/composables/images.js";
import VideoView from "@/components/VideoView.vue";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const props = defineProps({
  title: {type: String, required: true},
  content: {type: String, required: true},
  detail: {type: String, required: false},
  video: {type: Object, required: true},
  src: {type: Object, required: true},
  alt: {type: String, default: 'section image'},
  textLeft: {type: Boolean, default: true},
  callToAction: {type: String, default: ''},
  link: {type: String, default: ''},
  colors: {type: Object, required: true},
  backgroundVariant: {type: Number, default: 1},
  first: {type: Boolean, default: false},
});

const isActive = ref(false);
const isFullscreen = ref(false);

const calculatedClass = computed(() => {
  if (isFullscreen.value) return ' p-0 md:p-10';
  const rotations = ['rotate-1', 'rotate-2', 'rotate-2 md:rotate-3', 'rotate-2 md:rotate-4'];
  const negations = ['-rotate-1', '-rotate-2', '-rotate-2 md:-rotate-3', '-rotate-2 md:-rotate-4'];
  const deg = Math.floor(Math.random() * rotations.length);
  const rotation = props.backgroundVariant === 1 ? negations[deg] : rotations[deg];
  let style = props.backgroundVariant === 1 ? 'md:ml-40' : 'md:mr-40';
  style += props.first ? ' md:-mb-10 mt-10 ' : ` md:-my-10 ${rotation}`;
  return style + ' md:max-w-2xl px-4';
});

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
  if (isFullscreen.value) {
    document.body.style.overflow = 'hidden'; // Prevent parent (body) from scrolling
  } else {
    document.body.style.overflow = ''; // Restore parent scrolling
  }
};

const closeOnEscape = (event) => {
  if (event.key === "Escape" && isFullscreen.value) {
    toggleFullscreen();
  }
};

onMounted(() => {
  window.addEventListener("keydown", closeOnEscape);
});

const gotoPage = () => {
  console.log("gotoPage");
  if (props.link) {
    router.push(back(props.link));
    document.body.style.overflow = ''; // Restore parent scrolling

  }
};

onUnmounted(() => {
  window.removeEventListener("keydown", closeOnEscape);
});
</script>

<style scoped>
.fullscreen {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  overflow-y: auto; /* Enable scrolling within the fullscreen component */
}
</style>
